import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { format, startOfWeek, addDays, addWeeks, subWeeks, isSameDay, isToday, isBefore, isSaturday, getDay } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { Check, Clock, Users, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import UserAvatar from '../components/UserAvatar';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const OfficePage = () => {
  const { user, getAuthHeader } = useAuth();
  const { t, i18n } = useTranslation();
  const [checkins, setCheckins] = useState([]);
  const [allCheckins, setAllCheckins] = useState([]);
  const [plannedTime, setPlannedTime] = useState('09:00');
  const [loading, setLoading] = useState(false);
  const [currentWeek, setCurrentWeek] = useState(() => {
    // If today is Saturday or Sunday, show next week
    const today = new Date();
    const dayOfWeek = getDay(today);
    if (dayOfWeek === 6 || dayOfWeek === 0) {
      return startOfWeek(addWeeks(today, 1), { weekStartsOn: 1 });
    }
    return startOfWeek(today, { weekStartsOn: 1 });
  });

  // Get week days (Monday to Friday) for current selected week
  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 5; i++) {
      days.push(addDays(currentWeek, i));
    }
    return days;
  };

  const weekDays = getWeekDays();
  const getLocale = () => i18n.language === 'de' ? de : enUS;

  // Check if we're showing the current week
  const isCurrentWeek = () => {
    const today = new Date();
    const todayWeekStart = startOfWeek(today, { weekStartsOn: 1 });
    return format(currentWeek, 'yyyy-MM-dd') === format(todayWeekStart, 'yyyy-MM-dd');
  };

  // Check if we're showing a past week
  const isPastWeek = () => {
    const today = new Date();
    const todayWeekStart = startOfWeek(today, { weekStartsOn: 1 });
    return isBefore(currentWeek, todayWeekStart);
  };

  const fetchCheckins = useCallback(async () => {
    try {
      // Fetch checkins for the date range of the current week view
      const startDate = format(currentWeek, 'yyyy-MM-dd');
      const endDate = format(addDays(currentWeek, 4), 'yyyy-MM-dd');
      
      const response = await axios.get(`${API_URL}/office/checkins?start_date=${startDate}&end_date=${endDate}`, {
        headers: getAuthHeader()
      });
      setAllCheckins(response.data);
      // Filter only current user's checkins
      const myCheckins = response.data.filter(c => c.user_id === user?.id);
      setCheckins(myCheckins);
    } catch (error) {
      console.error('Failed to fetch office check-ins:', error);
    }
  }, [currentWeek, getAuthHeader, user?.id]);

  useEffect(() => {
    fetchCheckins();
  }, [fetchCheckins]);

  const isDateCheckedIn = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return checkins.some(c => c.date === dateStr);
  };

  const getCheckinForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return checkins.find(c => c.date === dateStr);
  };

  const getTeamCheckinsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return allCheckins.filter(c => c.date === dateStr);
  };

  const handleToggleDay = async (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const existingCheckin = getCheckinForDate(date);

    // Don't allow past dates
    if (isBefore(date, new Date()) && !isToday(date)) {
      return;
    }

    setLoading(true);

    try {
      if (existingCheckin) {
        // Remove checkin
        await axios.delete(`${API_URL}/office/checkins/${existingCheckin.id}`, {
          headers: getAuthHeader()
        });
        toast.success(t('office.removed'));
      } else {
        // Add checkin
        await axios.post(
          `${API_URL}/office/checkins`,
          {
            date: dateStr,
            planned_time: plannedTime || null
          },
          { headers: getAuthHeader() }
        );
        toast.success(t('office.success'));
      }
      fetchCheckins();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('office.error'));
    } finally {
      setLoading(false);
    }
  };

  const getDayName = (date) => {
    return format(date, 'EEEE', { locale: getLocale() });
  };

  const getDayNumber = (date) => {
    return format(date, 'd');
  };

  const handlePrevWeek = () => setCurrentWeek(subWeeks(currentWeek, 1));
  const handleNextWeek = () => setCurrentWeek(addWeeks(currentWeek, 1));
  const handleCurrentWeek = () => {
    const today = new Date();
    const dayOfWeek = getDay(today);
    if (dayOfWeek === 6 || dayOfWeek === 0) {
      setCurrentWeek(startOfWeek(addWeeks(today, 1), { weekStartsOn: 1 }));
    } else {
      setCurrentWeek(startOfWeek(today, { weekStartsOn: 1 }));
    }
  };

  // Calculate week number label
  const getWeekLabel = () => {
    const weekNum = format(currentWeek, 'w');
    const monthName = format(currentWeek, 'MMMM', { locale: getLocale() });
    const year = format(currentWeek, 'yyyy');
    return `${t('office.week')} ${weekNum} · ${monthName} ${year}`;
  };

  return (
    <Layout>
      <div data-testid="office-page" className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('office.title')}
          </h1>
          <p className="text-zinc-600">{t('office.weekSubtitle')}</p>
        </div>

        {/* Week Navigation */}
        <div className="flex items-center justify-between bg-white p-3 rounded-2xl border border-zinc-200 mb-4">
          <Button variant="ghost" size="sm" onClick={handlePrevWeek} className="rounded-full">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          
          <div className="text-center flex-1">
            <div className="font-semibold text-zinc-900">{getWeekLabel()}</div>
            <div className="text-xs text-zinc-500">
              {format(currentWeek, 'd. MMM', { locale: getLocale() })} - {format(addDays(currentWeek, 4), 'd. MMM', { locale: getLocale() })}
            </div>
          </div>
          
          <div className="flex items-center gap-1">
            {!isCurrentWeek() && (
              <Button variant="outline" size="sm" onClick={handleCurrentWeek} className="rounded-full text-xs mr-1">
                <RefreshCw className="w-4 h-4 mr-1" />
                {t('office.thisWeek')}
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={handleNextWeek} className="rounded-full">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Time Selector */}
        <div className="bg-white p-4 rounded-2xl border border-zinc-200 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-zinc-600">
              <Clock className="w-5 h-5" />
              <span className="text-sm font-medium">{t('office.defaultTime')}</span>
            </div>
            <Input
              type="time"
              value={plannedTime}
              onChange={(e) => setPlannedTime(e.target.value)}
              className="w-32 text-center"
              data-testid="default-time-input"
            />
          </div>
        </div>

        {/* Week View */}
        <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden mb-4">
          <div className="divide-y divide-zinc-100">
            {weekDays.map((day, index) => {
              const isCheckedIn = isDateCheckedIn(day);
              const isPast = isBefore(day, new Date()) && !isToday(day);
              const teamCheckins = getTeamCheckinsForDate(day);
              const checkin = getCheckinForDate(day);
              const isFutureWeek = !isCurrentWeek() && !isPastWeek();

              return (
                <motion.div
                  key={format(day, 'yyyy-MM-dd')}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`p-4 ${isPast ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Day Circle */}
                      <div className={`w-14 h-14 rounded-xl flex flex-col items-center justify-center ${
                        isToday(day) 
                          ? 'bg-[#D93025] text-white' 
                          : isCheckedIn 
                            ? 'bg-[#D1FAE5] text-[#047857]' 
                            : 'bg-zinc-100 text-zinc-600'
                      }`}>
                        <span className="text-xs font-medium uppercase">
                          {getDayName(day).slice(0, 2)}
                        </span>
                        <span className="text-xl font-bold">
                          {getDayNumber(day)}
                        </span>
                      </div>
                      
                      {/* Day Info */}
                      <div>
                        <div className="font-medium text-zinc-900">
                          {getDayName(day)}
                          {isToday(day) && (
                            <span className="ml-2 text-xs bg-[#D93025] text-white px-2 py-0.5 rounded-full">
                              {t('office.today')}
                            </span>
                          )}
                        </div>
                        {isCheckedIn && checkin?.planned_time && (
                          <div className="text-sm text-zinc-500">
                            {t('office.at')} {checkin.planned_time}
                          </div>
                        )}
                        {teamCheckins.length > 0 && (
                          <div className="flex items-center gap-1 text-xs text-zinc-500 mt-1">
                            <Users className="w-3 h-3" />
                            <span>{teamCheckins.length} {t('office.colleagues')}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Toggle Button - Allow for future weeks too */}
                    {(!isPast || isFutureWeek) && (
                      <Button
                        onClick={() => handleToggleDay(day)}
                        disabled={loading}
                        variant={isCheckedIn ? "default" : "outline"}
                        className={`rounded-full px-4 ${
                          isCheckedIn 
                            ? 'bg-[#047857] hover:bg-[#065F46] text-white' 
                            : 'border-zinc-300 hover:border-[#D93025] hover:bg-[#FEE2E2]'
                        }`}
                        data-testid={`toggle-day-${index}`}
                      >
                        {isCheckedIn ? (
                          <>
                            <Check className="w-4 h-4 mr-1" />
                            {t('office.planned')}
                          </>
                        ) : (
                          t('office.plan')
                        )}
                      </Button>
                    )}
                    {isPast && !isFutureWeek && isCheckedIn && (
                      <span className="text-sm text-[#047857] font-medium flex items-center gap-1">
                        <Check className="w-4 h-4" />
                        {t('office.wasInOffice')}
                      </span>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Team Overview */}
        <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
          <div className="p-4 border-b border-zinc-200 bg-zinc-50">
            <h2 className="font-semibold text-zinc-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-[#D93025]" />
              {t('office.whoIsInOffice')}
            </h2>
          </div>
          <div className="p-4 space-y-4">
            {weekDays.map((day, dayIndex) => {
              const teamCheckins = getTeamCheckinsForDate(day);
              // Only show days that have checkins
              if (teamCheckins.length === 0) return null;
              
              return (
                <div key={format(day, 'yyyy-MM-dd')} className={dayIndex > 0 ? 'pt-4 border-t border-zinc-100' : ''}>
                  <div className="text-sm font-medium text-zinc-600 mb-2">
                    {isToday(day) ? t('office.today') : getDayName(day)} - {format(day, 'd. MMM', { locale: getLocale() })}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {teamCheckins.map((checkin) => (
                      <div
                        key={checkin.id}
                        className="flex items-center gap-2 bg-zinc-100 rounded-full px-3 py-1"
                      >
                        <UserAvatar user={{ id: checkin.user_id, name: checkin.user_name }} size="sm" />
                        <span className="text-sm text-zinc-700">{checkin.user_name}</span>
                        {checkin.planned_time && (
                          <span className="text-xs text-zinc-500">{checkin.planned_time}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {weekDays.every(day => getTeamCheckinsForDate(day).length === 0) && (
              <p className="text-sm text-zinc-400 text-center py-4">{t('office.noOneYet')}</p>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default OfficePage;
